import { useState, useEffect } from 'react';
import {
  Layout,
  Card,
  Row,
  Col,
  Statistic,
  Button,
  Typography,
  Avatar,
  Dropdown,
  Space,
  Table,
  Tag,
  message
} from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  HomeOutlined,
  TeamOutlined,
  ExperimentOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  BookOutlined,
  EyeOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

export default function FacilitatorDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    active_sessions: 0,
    pending_evaluations: 0,
    total_students: 0,
    active_trainings: 0
  });
  const [activeSessions, setActiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, sessionsRes] = await Promise.allSettled([
        api.get('/facilitator/dashboard/stats'),
        api.get('/facilitator/sessions/active')
      ]);

      if (statsRes.status === 'fulfilled') {
        setStats(statsRes.value.data);
      }

      if (sessionsRes.status === 'fulfilled') {
        setActiveSessions(sessionsRes.value.data);
      }
    } catch (error) {
      message.error('Failed to load dashboard data');
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'dashboard',
      label: 'Dashboard',
      icon: <HomeOutlined />,
      onClick: () => navigate('/facilitator/dashboard'),
    },
    {
      key: 'profile',
      label: 'Profile',
      icon: <UserOutlined />,
      onClick: () => navigate('/facilitator/profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: 'Logout',
      icon: <LogoutOutlined />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  const sessionColumns = [
    {
      title: 'Student',
      key: 'student',
      render: (_, record) => (
        <Space>
          <Avatar style={{ backgroundColor: '#faad14' }} icon={<UserOutlined />} />
          <div>
            <div><Text strong>{`${record.user.first_name} ${record.user.last_name}`}</Text></div>
            <div><Text type="secondary" style={{ fontSize: 12 }}>{record.user.email}</Text></div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Lab',
      dataIndex: ['lab', 'name'],
      key: 'lab',
    },
    {
      title: 'Training',
      dataIndex: ['training', 'name'],
      key: 'training',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'orange'}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Messages',
      dataIndex: 'message_count',
      key: 'message_count',
      render: (count) => `${count} messages`,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/facilitator/monitor/${record.id}`)}
        >
          Monitor
        </Button>
      ),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{
        background: '#fff',
        padding: '0 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Title level={3} style={{ margin: 0, color: '#faad14', cursor: 'pointer' }} onClick={() => navigate('/facilitator/dashboard')}>
            USSEDU Platform
          </Title>
          <Text type="secondary">Facilitator Dashboard</Text>
        </div>
        <Space>
          <Text strong>{user?.email}</Text>
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Avatar style={{ backgroundColor: '#faad14', cursor: 'pointer' }} icon={<UserOutlined />} />
          </Dropdown>
        </Space>
      </Header>

      <Content style={{ padding: '24px', background: '#f0f2f5' }}>
        <div style={{ marginBottom: 24 }}>
          <Title level={2}>Welcome back, {user?.first_name}!</Title>
          <Text type="secondary">Monitor active sessions and manage your classrooms</Text>
        </div>

        {/* Statistics Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Active Sessions"
                value={stats.active_sessions}
                prefix={<ClockCircleOutlined style={{ color: '#faad14' }} />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Pending Evaluations"
                value={stats.pending_evaluations}
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Students"
                value={stats.total_students}
                prefix={<TeamOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Active Trainings"
                value={stats.active_trainings}
                prefix={<BookOutlined style={{ color: '#722ed1' }} />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Quick Actions */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card hoverable onClick={() => navigate('/facilitator/classrooms')} style={{ textAlign: 'center' }}>
              <ExperimentOutlined style={{ fontSize: 48, color: '#faad14', marginBottom: 16 }} />
              <Title level={4}>Manage Classrooms</Title>
              <Text type="secondary">Assign labs and unlock for students</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card hoverable onClick={() => navigate('/facilitator/sessions')} style={{ textAlign: 'center' }}>
              <EyeOutlined style={{ fontSize: 48, color: '#1890ff', marginBottom: 16 }} />
              <Title level={4}>Monitor Sessions</Title>
              <Text type="secondary">View active lab sessions in real-time</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card hoverable onClick={() => navigate('/facilitator/evaluations')} style={{ textAlign: 'center' }}>
              <FileTextOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
              <Title level={4}>Review Evaluations</Title>
              <Text type="secondary">Review and release student evaluations</Text>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card hoverable onClick={() => navigate('/facilitator/classrooms')} style={{ textAlign: 'center' }}>
              <TeamOutlined style={{ fontSize: 48, color: '#722ed1', marginBottom: 16 }} />
              <Title level={4}>Student Progress</Title>
              <Text type="secondary">Track student lab completion</Text>
            </Card>
          </Col>
        </Row>

        {/* Active Sessions Table */}
        <Card title="Active Lab Sessions" extra={
          <Button type="primary" onClick={() => navigate('/facilitator/sessions')}>
            View All
          </Button>
        }>
          <Table
            columns={sessionColumns}
            dataSource={activeSessions}
            loading={loading}
            rowKey="id"
            pagination={{ pageSize: 5 }}
          />
        </Card>
      </Content>
    </Layout>
  );
}
